import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { loginPathWithRedirect } from '../utils/authRedirect';
import ImageGallery from '../components/ImageGallery';
import ContactOwner from '../components/ContactOwner';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import './PropertyDetailPage.css';

function normalizeIndiaCoordinates(latValue, lngValue) {
  const lat = Number(latValue);
  const lng = Number(lngValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { lat: null, lng: null };
  }

  const inIndia = (xLat, xLng) => xLat >= 6 && xLat <= 38 && xLng >= 68 && xLng <= 98;

  if (inIndia(lat, lng)) {
    return { lat, lng };
  }

  // Legacy safeguard: if stored as lat/lng swapped, auto-correct for map rendering.
  if (inIndia(lng, lat)) {
    return { lat: lng, lng: lat };
  }

  return { lat, lng };
}

function collapseRepeatedCombinedAddress(rawValue) {
  const text = String(rawValue || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  const repeatedPattern = /^(.*?),\s*\1$/;
  const match = text.match(repeatedPattern);
  return match ? match[1].trim() : text;
}

function formatAddressForDisplay(rawValue) {
  const normalized = collapseRepeatedCombinedAddress(rawValue);
  if (!normalized) return '';

  const indiaMarker = ', India';
  const indiaIndex = normalized.indexOf(indiaMarker);
  if (indiaIndex === -1) return normalized;

  const mapsPart = normalized.slice(0, indiaIndex + indiaMarker.length).trim();
  const trailingPart = normalized.slice(indiaIndex + indiaMarker.length).replace(/^[,\s]+/, '').trim();

  // Legacy format was "maps address, house/apartment". Display as requested: "house/apartment, maps address".
  if (trailingPart) {
    return `${trailingPart}, ${mapsPart}`;
  }

  return normalized;
}

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const contactOwnerSectionRef = useRef(null);
  const user = api.getUser();

  useEffect(() => {
    fetchProperty(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
  }, [id]);

  useEffect(() => {
    if (user?.user_id && id) {
      api.checkSavedProperty(user.user_id, id).then(({ saved }) => setIsSaved(!!saved)).catch(() => setIsSaved(false));
    } else {
      setIsSaved(false);
    }
  }, [user?.user_id, id]);

  const mapBackendToFrontend = (p) => {
    if (!p) return null;
    const normalizedCoords = normalizeIndiaCoordinates(p.lat, p.lng);
    // Fallback for legacy rows where invalid ENUM values were saved as '' before enum migration.
    const normalizedPropertyType =
      p.property_type || (String(p.bhk_type || '').toLowerCase() === '1rk' ? 'pg' : '');
    const formattedAddress = formatAddressForDisplay(p.address_text || '');
    return {
      id: p.property_id,
      ownerId: p.owner_id,
      ownerProfileId: p.owner_profile_id,
      ownershipMode: p.ownership_mode || 'registered_owner',
      chatOwnerUserId: p.chat_owner_user_id || null,
      propertyFor: p.property_for,
      propertyType: normalizedPropertyType,
      bhk: p.bhk_type,
      // Backend stores combined address details in address_text.
      address: formattedAddress,
      locality: p.locality,
      city: p.city,
      state: p.state,
      pincode: p.pincode,
      builtUpArea: p.built_up_area,
      carpetArea: p.carpet_area,
      totalFloors: p.total_floors,
      floorNumber: p.floor_number,
      bedrooms: p.bedrooms,
      bathrooms: p.bathrooms,
      balconies: p.balconies,
      propertyAge: p.property_age,
      furnishing: p.furnishing,
      facing: p.facing || '',
      expectedPrice: p.expected_price,
      priceNegotiable: !!p.price_negotiable,
      maintenanceCharges: p.maintenance_charges,
      securityDeposit: p.security_deposit,
      description: p.description || '',
      availableFrom: p.available_from,
      ownerName: p.display_owner_name || p.owner_name || '',
      ownerEmail: p.owner_email,
      ownerPhone: p.display_owner_phone || p.owner_phone || '',
      ownerSecondaryPhone:
        p.secondary_phone_number ||
        p.owner_secondary_phone ||
        p.secondaryPhoneNumber ||
        '',
      status: p.status || 'active',
      isRentedOut: Number(p.is_rented_out || 0) === 1,
      rentedOutBy: p.rented_out_by || '',
      images: p.images || [],
      amenities: p.amenities || [],
      lat: normalizedCoords.lat,
      lng: normalizedCoords.lng,
    };
  };

  const isClosedProperty =
    property &&
    (property.status === 'inactive' ||
      property.status === 'rented' ||
      property.status === 'sold' ||
      property.isRentedOut);
  const closedPropertyLabel = property?.propertyFor === 'rent' ? 'Rented Out' : 'Sold Out';
  const closedWatermarkLabel = property?.propertyFor === 'rent' ? 'RENTED OUT' : 'SOLD OUT';
  const canEditProperty =
    Boolean(user && property) &&
    (
      (property.ownerId && Number(user.user_id) === Number(property.ownerId))
      || (
        Number(user?.is_admin || 0) === 1
        && property.ownershipMode === 'phantom_owner'
        && property.chatOwnerUserId != null
        && Number(user.user_id) === Number(property.chatOwnerUserId)
      )
    );

  const fetchProperty = async (propertyId) => {
    if (!propertyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getProperty(propertyId);
      setProperty(mapBackendToFrontend(data.data));
    } catch (err) {
      console.error('Fetch property error:', err);
      setProperty(null);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price, propertyFor) => {
    const num = parseInt(price);
    if (propertyFor === 'rent') {
      if (num >= 100000) {
        return `₹${(num / 100000).toFixed(1)}L`;
      } else if (num >= 1000) {
        return `₹${(num / 1000).toFixed(0)}K`;
      }
      return `₹${num}`;
    } else {
      if (num >= 10000000) {
        return `₹${(num / 10000000).toFixed(2)} Cr`;
      } else if (num >= 100000) {
        return `₹${(num / 100000).toFixed(1)} L`;
      }
      return `₹${num}`;
    }
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      'apartment': 'Apartment',
      'independent-house': 'Independent House',
      'villa': 'Villa',
      'builder-floor': 'Builder Floor',
      'studio': 'Studio',
      'penthouse': 'Penthouse',
      'commercial': 'Commercial Space',
      'pg': 'PG'
    };
    return labels[type] || type;
  };

  const getBhkLabel = (bhkValue) => {
    if (!bhkValue) return '';
    return String(bhkValue).toLowerCase() === '1rk' ? '1 RK' : `${bhkValue} BHK`;
  };

  const getAmenityLabel = (amenity) => {
    return amenity.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPropertyAgeLabel = (age) => {
    const labels = {
      '0-1': 'Less than 1 year',
      '1-3': '1-3 years',
      '3-5': '3-5 years',
      '5-10': '5-10 years',
      '10+': '10+ years',
    };
    return labels[age] || '';
  };

  const hasDisplayValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
  };

  const formatAreaValue = (value) => {
    if (!hasDisplayValue(value)) return '';
    return `${value} sq ft`;
  };

  const formatFurnishingLabel = (value) => {
    const labels = {
      'fully-furnished': 'Fully Furnished',
      'semi-furnished': 'Semi Furnished',
      'unfurnished': 'Unfurnished',
    };
    return labels[value] || '';
  };

  const formatFloorValue = (floorNumber, totalFloors) => {
    if (!hasDisplayValue(floorNumber) || !hasDisplayValue(totalFloors)) return '';
    const floorLabel = Number(floorNumber) === 0 ? 'Ground' : floorNumber;
    return `${floorLabel} of ${totalFloors}`;
  };

  const propertyMapEmbedUrl = useMemo(() => {
    if (!property) return '';
    const hasCoordinates =
      Number.isFinite(property.lat) &&
      Number.isFinite(property.lng) &&
      property.lat >= -90 &&
      property.lat <= 90 &&
      property.lng >= -180 &&
      property.lng <= 180;
    if (hasCoordinates) {
      return `https://www.google.com/maps?q=${property.lat},${property.lng}&z=16&output=embed`;
    }
    const query = String(property.address || '').trim();
    if (!query) return '';
    return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
  }, [property]);

  const bhkLabel = getBhkLabel(property?.bhk);
  const builtUpAreaLabel = formatAreaValue(property?.builtUpArea);
  const carpetAreaLabel = formatAreaValue(property?.carpetArea);
  const furnishingLabel = formatFurnishingLabel(property?.furnishing);
  const floorLabel = formatFloorValue(property?.floorNumber, property?.totalFloors);
  const propertyAgeLabel = getPropertyAgeLabel(property?.propertyAge);
  const facingLabel = hasDisplayValue(property?.facing)
    ? property.facing.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-')
    : '';
  const propertyTypeLabel = getPropertyTypeLabel(property?.propertyType);

  const handleSaveClick = async () => {
    if (!user?.user_id) {
      navigate(loginPathWithRedirect(location));
      return;
    }
    setSaveLoading(true);
    try {
      if (isSaved) {
        await api.unsaveProperty(user.user_id, id);
        setIsSaved(false);
      } else {
        await api.saveProperty(user.user_id, id);
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Save/unsave error:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleShareProperty = async () => {
    if (!property) return;
    const url = `${window.location.origin}${location.pathname}`;
    const title = `${getBhkLabel(property.bhk)} ${getPropertyTypeLabel(property.propertyType)} | HimHomes`;
    const priceLine = formatPrice(property.expectedPrice, property.propertyFor);
    const rentSuffix = property.propertyFor === 'rent' ? ' / month' : '';
    const place = [property.locality, property.city].filter(Boolean).join(', ');
    const text = place ? `${priceLine}${rentSuffix} · ${place}` : `${priceLine}${rentSuffix}`;

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied — paste anywhere to share');
        return;
      } catch (_) {
        /* fall through */
      }
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast('Link copied — paste anywhere to share');
    } catch (_) {
      window.prompt('Copy this link to share:', url);
    }
  };

  const scrollToContactOwner = () => {
    if (!contactOwnerSectionRef.current) return;
    contactOwnerSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="property-detail-page">
        <div className="property-detail-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading property details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!property && !isLoading) {
    return (
      <div className="property-detail-page">
        <div className="property-detail-container">
          <div className="error-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h2>Property Not Found</h2>
            <p>{error || 'The property you\'re looking for doesn\'t exist or has been removed.'}</p>
            <button className="back-btn" onClick={() => navigate('/properties')}>
              Back to Properties
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="property-detail-page">
      <div className="property-detail-container">
        <div className="property-detail-top">
          <button type="button" className="back-link" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div className="property-badge">
            {property.propertyFor === 'rent' ? 'For Rent' : 'For Sale'}
          </div>
          {isClosedProperty && (
            <div className="property-badge property-badge--closed">
              {closedPropertyLabel}
            </div>
          )}
        </div>

        {/* Property Header */}
        <div className="property-header">
          <div className="header-main">
            <h1 className="property-title">
              {getBhkLabel(property.bhk)} {getPropertyTypeLabel(property.propertyType)}
            </h1>
            <div className="property-location">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {property.address}
            </div>
          </div>
          <div className="header-price">
            <div className="price-block">
              <div className="price-amount">
                {formatPrice(property.expectedPrice, property.propertyFor)}
              </div>
              {property.propertyFor === 'rent' && (
                <div className="price-period">per month</div>
              )}
              {property.priceNegotiable && (
                <div className="negotiable-tag">Negotiable</div>
              )}
            </div>
            {canEditProperty && (
              <button
                type="button"
                className="edit-property-btn"
                onClick={() => navigate(`/properties/${id}/edit`)}
                aria-label="Edit property"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
            )}
            <div className="header-price__icon-actions">
              <button
                type="button"
                className="share-property-btn"
                onClick={handleShareProperty}
                aria-label="Share this property"
                title="Share"
              >
                <svg className="share-property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span className="share-property-tooltip">Share</span>
              </button>
              <div
                className={`save-property-btn ${isSaved ? 'saved' : ''}`}
                title={isSaved ? 'Saved' : 'Save'}
                onClick={handleSaveClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSaveClick(); } }}
                aria-label={isSaved ? 'Remove from saved' : 'Save property'}
              >
                {saveLoading ? (
                  <span className="save-property-spinner" />
                ) : (
                  <svg className="save-property-icon" viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                )}
                <span className="save-property-tooltip">{isSaved ? 'Saved' : 'Save'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="property-content">
          {/* Left Column */}
          <div className="property-main">
            {/* Image Gallery */}
            <ImageGallery images={property.images} />

            {/* Overview */}
            <section className="detail-section">
              <h2 className="section-title">Overview</h2>
              <div className="overview-grid">
                {hasDisplayValue(bhkLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    <div>
                      <span className="overview-label">BHK</span>
                      <span className="overview-value">{bhkLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(builtUpAreaLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    </svg>
                    <div>
                      <span className="overview-label">Built-up Area</span>
                      <span className="overview-value">{builtUpAreaLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(carpetAreaLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                    <div>
                      <span className="overview-label">Carpet Area</span>
                      <span className="overview-value">{carpetAreaLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(furnishingLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 9v11a2 2 0 01-2 2H6a2 2 0 01-2-2V9"/>
                      <path d="M9 22V12h6v10M2 10.6L12 2l10 8.6"/>
                    </svg>
                    <div>
                      <span className="overview-label">Furnishing</span>
                      <span className="overview-value">{furnishingLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(floorLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                    </svg>
                    <div>
                      <span className="overview-label">Floor</span>
                      <span className="overview-value">{floorLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(propertyAgeLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <div>
                      <span className="overview-label">Property Age</span>
                      <span className="overview-value">{propertyAgeLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(facingLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                    <div>
                      <span className="overview-label">Facing</span>
                      <span className="overview-value">{facingLabel}</span>
                    </div>
                  </div>
                )}

                {hasDisplayValue(propertyTypeLabel) && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                    </svg>
                    <div>
                      <span className="overview-label">Property Type</span>
                      <span className="overview-value">{propertyTypeLabel}</span>
                    </div>
                  </div>
                )}

                {property.availableFrom && (
                  <div className="overview-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <div>
                      <span className="overview-label">Available From</span>
                      <span className="overview-value">
                        {(() => {
                          const d = new Date(property.availableFrom);
                          return !Number.isNaN(d.getTime())
                            ? d.toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })
                            : property.availableFrom;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Location Map */}
            {propertyMapEmbedUrl && (
              <section className="detail-section">
                <h2 className="section-title">Location</h2>
                <div className="property-map-wrap">
                  <iframe
                    title="Property location map"
                    src={propertyMapEmbedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="property-map-iframe"
                  />
                </div>
              </section>
            )}

            {/* Description */}
            <section className="detail-section">
              <h2 className="section-title">Description</h2>
              <p className="property-description">{property.description}</p>
            </section>

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <section className="detail-section">
                <h2 className="section-title">Amenities</h2>
                <div className="amenities-list">
                  {property.amenities.map((amenity, index) => (
                    <div key={index} className="amenity-item">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {getAmenityLabel(amenity)}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Pricing Details */}
            {property.propertyFor === 'rent' && (
              <section className="detail-section">
                <h2 className="section-title">Pricing Details</h2>
                <div className="pricing-details">
                  <div className="pricing-row">
                    <span className="pricing-label">Monthly Rent</span>
                    <span className="pricing-value">
                      {formatPrice(property.expectedPrice, 'rent')}
                    </span>
                  </div>
                  {property.maintenanceCharges && (
                    <div className="pricing-row">
                      <span className="pricing-label">Maintenance</span>
                      <span className="pricing-value">
                        ₹{parseInt(property.maintenanceCharges).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  {property.securityDeposit && (
                    <div className="pricing-row">
                      <span className="pricing-label">Security Deposit</span>
                      <span className="pricing-value">
                        ₹{parseInt(property.securityDeposit).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  <div className="pricing-row pricing-row--total">
                    <span className="pricing-label">Total Move-in Cost</span>
                    <span className="pricing-value">
                      ₹{(
                        parseInt(property.expectedPrice || 0) +
                        parseInt(property.maintenanceCharges || 0) +
                        parseInt(property.securityDeposit || 0)
                      ).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Right Column - Contact Owner */}
          <aside className="property-sidebar" id="contact-owner-section" ref={contactOwnerSectionRef}>
            <ContactOwner owner={property} propertyId={property.id} ownerId={property.ownerId} />
          </aside>
        </div>
      </div>
      <button
        type="button"
        className="mobile-contact-owner-cta"
        onClick={scrollToContactOwner}
        aria-label="Scroll to Contact Owner section"
      >
        Contact Owner
      </button>
      {isClosedProperty && (
        <div className="property-closed-watermark" aria-hidden="true">
          {closedWatermarkLabel}
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;